/**
 * Voucher Template Types
 * Templates for generating individual voucher instances
 */

export type ItemType = "voucher" | "ticket";

export type Subcategory = "signup" | "event" | "general" | null;

export type GenerationMode = "static_url" | "individual_codes" | "both";

export type ParameterType = "static" | "mapped" | "generated";

export interface URLParameterDefinition {
  type: ParameterType;
  label: string;

  // For static parameters
  value?: string;

  // For mapped parameters
  source?: string; // e.g., "organization.id", "location.state", "contact.email"

  // For generated parameters
  format?: string; // e.g., "alphanumeric_8", "uuid", "sequential"
}

export interface URLParameters {
  [parameterName: string]: URLParameterDefinition;
}

export interface OrganizationAssociation {
  org_id: string;
  org_name?: string; // For display purposes
  location_id: string | null;
  location_name?: string; // For display purposes
}

export interface VoucherTemplate {
  id: string;

  // Basic Information
  voucher_name: string;
  item_type: ItemType;
  subcategory?: Subcategory;

  // Organization/Location associations
  organizations: OrganizationAssociation[];

  // Legacy and pricing fields
  affid?: string | null;
  retailer_promo_code?: string | null;
  alt_price?: number | null;
  voucher_promo_code: string; // Accounting system identifier
  discount_value: number;

  // Geographic restrictions
  states: string[]; // Array of state abbreviations (e.g., ['TX', 'CA'])

  // URL generation configuration
  generation_mode: GenerationMode;
  url_base?: string | null;
  url_template?: string | null;
  url_parameters?: URLParameters | null;

  // QR Code (legacy fields - kept for backward compatibility)
  qr_code_url?: string | null;
  qr_code_image_url?: string | null;

  // Metadata
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;
}

export interface CreateVoucherTemplateDTO {
  voucher_name: string;
  item_type: ItemType;
  subcategory?: Subcategory;
  organizations: OrganizationAssociation[];
  affid?: string;
  retailer_promo_code?: string;
  alt_price?: number;
  voucher_promo_code: string;
  discount_value: number;
  states: string[];

  // URL generation fields
  generation_mode: GenerationMode;
  url_base?: string;
  url_template?: string;
  url_parameters?: URLParameters;

  // Legacy QR code fields (optional)
  qr_code_url?: string;
}

export interface UpdateVoucherTemplateDTO {
  voucher_name?: string;
  item_type?: ItemType;
  subcategory?: Subcategory;
  organizations?: OrganizationAssociation[];
  affid?: string;
  retailer_promo_code?: string;
  alt_price?: number;
  voucher_promo_code?: string;
  discount_value?: number;
  states?: string[];

  // URL generation fields
  generation_mode?: GenerationMode;
  url_base?: string;
  url_template?: string;
  url_parameters?: URLParameters;

  // Legacy QR code fields
  qr_code_url?: string;
}

// US States for multi-select dropdown
export const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
];

// Item Type Options
export const ITEM_TYPE_OPTIONS = [
  {
    value: "voucher" as ItemType,
    label: "Voucher",
    description: "Discount or promotional voucher",
  },
  {
    value: "ticket" as ItemType,
    label: "Ticket",
    description: "Event admission ticket",
  },
];

// Subcategory Options
export const SUBCATEGORY_OPTIONS = [
  {
    value: "signup" as Subcategory,
    label: "Signup",
    description: "For new membership signups",
  },
  {
    value: "event" as Subcategory,
    label: "Event",
    description: "Event-specific items",
  },
  {
    value: "general" as Subcategory,
    label: "General",
    description: "General promotional items",
  },
];

// Generation Mode Options
export const GENERATION_MODE_OPTIONS = [
  {
    value: "static_url" as GenerationMode,
    label: "Static URL",
    description: "Same URL for all (e.g., store QR codes)",
  },
  {
    value: "individual_codes" as GenerationMode,
    label: "Individual Codes",
    description: "Unique codes via API (e.g., membership vouchers)",
  },
  {
    value: "both" as GenerationMode,
    label: "Both",
    description: "Support both static URLs and individual codes",
  },
];

// Available parameter sources for mapped parameters
export const PARAMETER_SOURCES = {
  organization: [
    { value: "organization.id", label: "Organization ID" },
    { value: "organization.name", label: "Organization Name" },
    { value: "organization.partner_code", label: "Partner Code" },
    { value: "organization.facility_id", label: "Facility ID" },
  ],
  location: [
    { value: "location.id", label: "Location ID" },
    { value: "location.name", label: "Location Name" },
    { value: "location.state", label: "State Code" },
    { value: "location.city", label: "City" },
    { value: "location.zip_code", label: "Zip Code" },
  ],
  contact: [
    { value: "contact.id", label: "Contact ID" },
    { value: "contact.first_name", label: "First Name" },
    { value: "contact.last_name", label: "Last Name" },
    { value: "contact.email", label: "Email" },
    { value: "contact.membership_id", label: "Member Number" },
  ],
};

// Available code generation formats
export const GENERATION_FORMATS = [
  { value: "alphanumeric", label: "Alphanumeric (A-Z, 0-9)" },
  { value: "numeric", label: "Numeric (0-9)" },
  { value: "alpha", label: "Alpha (A-Z)" },
  { value: "uuid", label: "UUID" },
  { value: "custom", label: "Custom Format" },
];

// Available voucher types
export const VOUCHER_TYPE_OPTIONS = [
  { value: "discount", label: "Discount" },
  { value: "freebie", label: "Free Item/Service" },
  { value: "bogo", label: "Buy One Get One" },
  { value: "membership", label: "Membership" },
  { value: "trial", label: "Free Trial" },
  { value: "other", label: "Other" },
];
